import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AuthLayout } from '@/components/AuthLayout';
import { takeVerifier } from '@/security/sso';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Échanges en cours, par code : le code ne sert qu'une fois, et React rejoue les effets en développement (StrictMode)
 */
const exchanges = new Map<string, Promise<void>>();

/**
 * Retour de la connexion Okta (/sso/callback?code=…, cf. src/security/sso.ts) : échange le code contre la session, puis
 * l'accueil (définition du mot de passe de secours d'abord s'il n'en a jamais défini, RequireAuth). Échec (code expiré,
 * déjà utilisé, onglet différent) : retour à la page de connexion avec le message d'échec
 */
export function SsoCallbackPage() {
  const [searchParams] = useSearchParams();
  const loginWithOkta = useAuthStore((state) => state.loginWithOkta);
  const navigate = useNavigate();
  const code = searchParams.get('code') ?? '';

  useEffect(() => {
    let exchange = exchanges.get(code);
    if (!exchange) {
      const verifier = takeVerifier();
      exchange =
        code && verifier ? loginWithOkta(code, verifier) : Promise.reject(new Error('Connexion Okta incomplète'));
      exchanges.set(code, exchange);
    }

    let active = true;
    exchange.then(
      () => active && navigate('/', { replace: true }),
      () => active && navigate('/login?sso_error=failed', { replace: true }),
    );
    return () => {
      active = false;
    };
  }, [code, loginWithOkta, navigate]);

  return (
    <AuthLayout title="Connexion" subtitle="Connexion avec Okta en cours…">
      <p role="status" className="text-sm text-slate-600 text-center">
        Chargement…
      </p>
    </AuthLayout>
  );
}
